import { EnvironmentConfig } from '../types/EnvironmentConfig.types'
import { WorkspaceSettings } from '../../../../../../core/models/Workspace'
import {
  DEFAULT_SEVV_THEME_TOKENS,
  mergeThemeTokens,
  parseJsonField,
  stringifyJsonField,
  themeTokensFromLegacyUrls,
  type BrandingExtension,
  type SevvThemeTokens,
} from '../../../../../../theme/sevvThemeTokenModel'
import { defaultEnvironmentConfig } from './defaultConfigs'
import type { AppearanceConfig } from '../types/EnvironmentConfig.types'

function mergeAppearance(base: AppearanceConfig, partial: Partial<AppearanceConfig>): AppearanceConfig {
  return {
    theme: { ...base.theme, ...partial.theme },
    typography: { ...base.typography, ...partial.typography },
    animations: { ...base.animations, ...partial.animations },
    localization: { ...base.localization, ...partial.localization },
  }
}

/**
 * Maps EnvironmentConfig to WorkspaceSettings format
 */
export const mapEnvironmentConfigToWorkspaceSettings = (config: EnvironmentConfig): Partial<WorkspaceSettings> => {
  const tokensToSave = mergeThemeTokens(config.designTokens, {
    palette: {
      ...config.designTokens.palette,
      primary: config.branding.primaryColor,
      secondary: config.branding.secondaryColor,
    },
  })

  const brandingExtension: BrandingExtension = {
    faviconUrl: config.branding.faviconUrl,
    platformDescription: config.branding.platformDescription,
    logoAltText: config.branding.logoAltText,
    accentColor: config.branding.accentColor,
    textColor: config.branding.textColor,
    backgroundColor: config.branding.backgroundColor,
    authDialogPlatformName: config.branding.authDialogPlatformName,
    authDialogLogoUrl: config.branding.authDialogLogoUrl,
  }

  return {
    name: config.branding?.platformName || 'Platform',
    platform_name: config.branding?.platformName || 'Platform',
    platform_url: config.branding?.platformUrl || '',
    logo_url: config.branding?.logoUrl || '/logo.png',
    blocked_domains: config.registration.emailDomains.blocked || [],
    registration_enabled: config.registration.mode === 'open',
    // Always sent, so clearing the field clears it in cloud too
    waitlist_url: config.registration.waitlistUrl?.trim() || '',
    appearance_config: stringifyJsonField(config.appearance),
    theme_tokens: stringifyJsonField(tokensToSave),
    branding_extension: stringifyJsonField(brandingExtension),
    urls: {
      primaryColor: config.branding?.primaryColor || '#389acb',
      secondaryColor: config.branding?.secondaryColor || '#2c3e50',
      accentColor: config.branding?.accentColor || '#e74c3c',
      textColor: config.branding?.textColor || '#424242',
      backgroundColor: config.branding?.backgroundColor || '#fafafa',
      ...(config.integrations?.email?.provider
        ? {
            emailConfig: JSON.stringify({
              provider: config.integrations.email.provider,
              ...(config.integrations.email.provider === 'smtp' && config.integrations.email.smtp
                ? {
                    host: config.integrations.email.smtp.host,
                    port: config.integrations.email.smtp.port,
                    username: config.integrations.email.smtp.username,
                    password: config.integrations.email.smtp.password,
                    secure: config.integrations.email.smtp.secure,
                    senderName:
                      config.integrations.email.smtp.senderName || (config.branding?.platformName || 'Platform'),
                    replyTo: config.integrations.email.smtp.replyTo || 'noreply@wiggin-industries.net',
                  }
                : {}),
              ...(config.integrations.email.provider === 'mailgun' && config.integrations.email.mailgun
                ? {
                    apiKey: config.integrations.email.mailgun.apiKey,
                    domain: config.integrations.email.mailgun.domain,
                    senderName:
                      config.integrations.email.mailgun.senderName || (config.branding?.platformName || 'Platform'),
                    replyTo: config.integrations.email.mailgun.replyTo || 'noreply@wiggin-industries.net',
                  }
                : {}),
              ...(config.integrations.email.provider === 'sendgrid' && config.integrations.email.sendgrid
                ? {
                    apiKey: config.integrations.email.sendgrid.apiKey,
                    senderName: config.branding?.platformName || 'Platform',
                    replyTo: 'noreply@wiggin-industries.net',
                  }
                : {}),
              ...(config.integrations.email.provider === 'aws-ses' && config.integrations.email.awsSes
                ? {
                    accessKeyId: config.integrations.email.awsSes.accessKeyId,
                    secretAccessKey: config.integrations.email.awsSes.secretAccessKey,
                    region: config.integrations.email.awsSes.region,
                    senderName: config.branding?.platformName || 'Platform',
                    replyTo: 'noreply@wiggin-industries.net',
                  }
                : {}),
            }),
          }
        : {}),
    },
    // SC-209: this used to be three hardcoded `true`s, so an admin who switched any feature off had it
    // silently switched back on the next time they saved anything at all on this screen.
    features: {
      ...defaultEnvironmentConfig.platform.features,
      ...(config.platform.features ?? {}),
    },
    restrictions: {
      maxRoomsPerUser: config.registration.limits.maxRooms,
      maxUsersPerRoom: config.registration.limits.maxUsers,
    },
  }
}

/**
 * Maps WorkspaceSettings to EnvironmentConfig format
 */
export const mapWorkspaceSettingsToEnvironmentConfig = (settings: WorkspaceSettings): Partial<EnvironmentConfig> => {
  const appearanceParsed = parseJsonField<Partial<AppearanceConfig>>(settings.appearance_config, {})
  const appearance = mergeAppearance(defaultEnvironmentConfig.appearance, appearanceParsed)

  let designTokens: SevvThemeTokens = mergeThemeTokens(
    DEFAULT_SEVV_THEME_TOKENS,
    themeTokensFromLegacyUrls(settings.urls) as Partial<SevvThemeTokens>
  )
  designTokens = mergeThemeTokens(
    designTokens,
    parseJsonField<Partial<SevvThemeTokens>>(settings.theme_tokens, {})
  )

  const ext = parseJsonField<BrandingExtension>(settings.branding_extension, {})

  const primary = designTokens.palette.primary || settings.urls?.primaryColor || '#389acb'
  const secondary = designTokens.palette.secondary || settings.urls?.secondaryColor || '#2c3e50'

  return {
    platform: {
      features: {
        ...defaultEnvironmentConfig.platform.features,
        ...(settings.features ?? {}),
      },
      contact: {
        email: 'support@sevv.com',
        phone: '',
        address: '',
        supportUrl: '',
      },
      social: {
        twitter: '',
        linkedin: '',
        github: '',
        facebook: '',
      },
    },
    branding: {
      platformName: settings.platform_name || settings.name || 'Platform',
      platformUrl: settings.platform_url || '',
      logoUrl: settings.logo_url || '/logo.png',
      primaryColor: primary,
      secondaryColor: secondary,
      platformDescription: ext.platformDescription,
      faviconUrl: ext.faviconUrl,
      logoAltText: ext.logoAltText,
      accentColor: ext.accentColor ?? settings.urls?.accentColor,
      textColor: ext.textColor ?? settings.urls?.textColor,
      backgroundColor: ext.backgroundColor ?? settings.urls?.backgroundColor,
      authDialogPlatformName: ext.authDialogPlatformName,
      authDialogLogoUrl: ext.authDialogLogoUrl,
    },
    designTokens,
    appearance,
    registration: {
      mode: settings.registration_enabled ? 'open' : 'closed',
      waitlistUrl: settings.waitlist_url || '',
      emailDomains: {
        allowed: [],
        blocked: settings.blocked_domains || [],
        requireVerification: true,
      },
      requiredFields: {
        firstName: true,
        lastName: true,
        email: true,
        phone: false,
        company: false,
        title: false,
      },
      verification: {
        emailVerification: true,
        phoneVerification: false,
        adminApproval: false,
        autoApprove: true,
      },
      terms: {
        enabled: false,
        url: '',
        requireAcceptance: false,
      },
      limits: {
        maxUsers: settings.restrictions?.maxUsersPerRoom ?? 50,
        maxRooms: settings.restrictions?.maxRoomsPerUser ?? 10,
        maxStorage: 100,
      },
    },
    security: {
      password: {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: false,
        maxAge: 90,
        preventReuse: 5,
      },
      session: {
        timeout: 30,
        maxConcurrent: 3,
        rememberMe: true,
        secureOnly: true,
      },
      rateLimiting: {
        enabled: true,
        requestsPerMinute: 60,
        requestsPerHour: 1000,
      },
      twoFactor: {
        enabled: false,
        required: false,
        methods: ['email'],
      },
      ipRestrictions: {
        enabled: false,
        whitelist: [],
        blacklist: [],
      },
      encryption: {
        algorithm: 'AES-256',
        keyRotation: 30,
      },
    },
    integrations: {
      analytics: {
        googleAnalytics: {
          enabled: false,
          trackingId: '',
        },
        mixpanel: {
          enabled: false,
          token: '',
        },
        hotjar: {
          enabled: false,
          siteId: '',
        },
      },
      email: (() => {
        const emailConfigStr = settings.urls?.emailConfig
        if (emailConfigStr) {
          try {
            const emailConfig = JSON.parse(emailConfigStr)
            const provider = emailConfig.provider || 'smtp'

            return {
              provider: provider,
              smtp: {
                host: emailConfig.host || '',
                port: emailConfig.port || 587,
                username: emailConfig.username || '',
                password: emailConfig.password || '',
                secure: emailConfig.secure || true,
                senderName: emailConfig.senderName || (settings.platform_name || settings.name || 'Platform'),
                replyTo: emailConfig.replyTo || 'noreply@wiggin-industries.net',
              },
              sendgrid: {
                apiKey: emailConfig.provider === 'sendgrid' ? (emailConfig.apiKey || '') : '',
              },
              mailgun: {
                apiKey: emailConfig.provider === 'mailgun' ? (emailConfig.apiKey || '') : '',
                domain: emailConfig.provider === 'mailgun' ? (emailConfig.domain || '') : '',
                senderName:
                  emailConfig.provider === 'mailgun'
                    ? (emailConfig.senderName || (settings.platform_name || settings.name || 'Platform'))
                    : '',
                replyTo:
                  emailConfig.provider === 'mailgun'
                    ? (emailConfig.replyTo || 'noreply@wiggin-industries.net')
                    : '',
              },
              awsSes: {
                accessKeyId: emailConfig.provider === 'aws-ses' ? (emailConfig.accessKeyId || '') : '',
                secretAccessKey: emailConfig.provider === 'aws-ses' ? (emailConfig.secretAccessKey || '') : '',
                region: emailConfig.provider === 'aws-ses' ? (emailConfig.region || '') : '',
              },
            }
          } catch (error) {
            console.warn('Failed to parse email config from workspace settings:', error)
          }
        }

        const smtpConfigStr = settings.urls?.smtpConfig
        if (smtpConfigStr) {
          try {
            const smtpConfig = JSON.parse(smtpConfigStr)
            return {
              provider: 'smtp',
              smtp: {
                host: smtpConfig.host || '',
                port: smtpConfig.port || 587,
                username: smtpConfig.username || '',
                password: smtpConfig.password || '',
                secure: smtpConfig.secure || true,
                senderName: smtpConfig.senderName || (settings.platform_name || settings.name || 'Platform'),
                replyTo: smtpConfig.replyTo || 'noreply@wiggin-industries.net',
              },
              sendgrid: {
                apiKey: '',
              },
              mailgun: {
                apiKey: '',
                domain: '',
              },
              awsSes: {
                accessKeyId: '',
                secretAccessKey: '',
                region: '',
              },
            }
          } catch (error) {
            console.warn('Failed to parse SMTP config from workspace settings:', error)
          }
        }

        return {
          provider: 'smtp',
          smtp: {
            host: '',
            port: 587,
            username: '',
            password: '',
            secure: true,
            senderName: settings.platform_name || settings.name || 'Platform',
            replyTo: 'noreply@wiggin-industries.net',
          },
          sendgrid: {
            apiKey: '',
          },
          mailgun: {
            apiKey: '',
            domain: '',
          },
          awsSes: {
            accessKeyId: '',
            secretAccessKey: '',
            region: '',
          },
        }
      })(),
      oauth: {
        google: {
          enabled: false,
          clientId: '',
          clientSecret: '',
        },
        github: {
          enabled: false,
          clientId: '',
          clientSecret: '',
        },
        microsoft: {
          enabled: false,
          clientId: '',
          clientSecret: '',
        },
      },
      webhooks: {
        enabled: false,
        endpoints: [],
      },
    },
    advanced: {
      performance: {
        caching: {
          enabled: true,
          ttl: 3600,
          maxSize: 100,
        },
        compression: {
          enabled: true,
          level: 6,
        },
        cdn: {
          enabled: false,
          url: '',
        },
      },
      logging: {
        level: 'info',
        file: {
          enabled: true,
          path: '/logs/app.log',
          maxSize: 10,
          maxFiles: 5,
        },
        console: {
          enabled: true,
        },
        external: {
          enabled: false,
          url: '',
          apiKey: '',
        },
      },
      monitoring: {
        healthChecks: {
          enabled: true,
          interval: 60,
        },
        metrics: {
          enabled: false,
          endpoint: '',
        },
        alerts: {
          enabled: false,
          email: [],
          webhook: '',
        },
      },
    },
  }
}
